import { ApolloError, ServerError } from "@apollo/client";
import { useMediaQuery, useTheme } from "@mui/material";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { PropsWithChildren, useCallback, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router";
import { useCustomerNavItems } from "routes/Customer";
import { useCustomerNavSectionsV2 } from "routes/Customer/customerNavSectionsV2";

import { LeftNavDrawer, NavItem } from "../components/LeftNavDrawer";
import { Main } from "../components/Main";
import { TopNavBar } from "../components/TopNavBar";
import { useThemeMode } from "../hooks/useThemeMode";
import { LayoutV2 } from "./LeftNavV2/LayoutV2";

function useHandleForbiddenTenant(error: ApolloError | undefined) {
  const navigate = useNavigate();

  useEffect(() => {
    // If the user is not authorized for this tenant, redirect to the home page.
    // This has the effect of auto switching to an authorized tenant.
    if (error && error.networkError && (error.networkError as ServerError).statusCode === 403) {
      navigate("/", { replace: true });
    }
  }, [error, navigate]);
}

export function Layout(props: PropsWithChildren) {
  const { featureFlags } = useFeatureFlags();
  const { error: errorV1, navItems: customerNavItemsV1 } = useCustomerNavItems();
  const { error: errorV2, navItems: customerNavSectionsV2 } = useCustomerNavSectionsV2();

  useHandleForbiddenTenant(errorV1);
  useHandleForbiddenTenant(errorV2);

  if (!featureFlags[FeatureFlagKey.LeftNavV2]) {
    return (
      <LayoutV1 navItems={customerNavItemsV1}>
        {props.children}
        <Outlet />
      </LayoutV1>
    );
  }

  return (
    <LayoutV2 navSections={customerNavSectionsV2}>
      {props.children}
      <Outlet />
    </LayoutV2>
  );
}

export function LayoutV1(props: PropsWithChildren<{ navItems: NavItem[] }>) {
  const { toggleThemeMode } = useThemeMode();
  const theme = useTheme();
  const drawerWidth = theme.spacing(25);
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [open, setOpen] = useState(isLargeScreen);

  useEffect(() => {
    setOpen(isLargeScreen);
  }, [isLargeScreen]);

  const handleDrawerToggle = useCallback(() => {
    setOpen(!open);
  }, [open, setOpen]);

  return (
    <>
      <LeftNavDrawer
        navItems={props.navItems}
        open={open}
        drawerWidth={drawerWidth}
        onClose={handleDrawerToggle}
      />
      <Main open={open} drawerWidth={drawerWidth}>
        <TopNavBar handleThemeToggle={toggleThemeMode} onMenuClick={handleDrawerToggle} />
        {props.children}
      </Main>
    </>
  );
}
